'use client';

import clsx from 'clsx';
import { useState, useRef, useEffect, useId } from 'react';
import styles from './Dropdown.module.scss';
import Icon, { type SvgIconInput } from '@publishing/components/ui/Icon';
import { ArrowChevron } from '@publishing/components/ui/SvgSprite';
import FormMessage from '@publishing/components/forms/_FormMessage';
import type { FieldSize } from '../formgroup/FormGroup';
import OptionList from './OptionList';

export interface DropdownOption {
  value: string;
  label: string;
  subtitle?: string;
  tag?: string;
  /** 같은 값이 연속될 때 한 번만 섹션 헤더로 표시 (모바일 시트·긴 목록에서 그룹 구분) */
  groupTitle?: string;
}

export interface DropdownProps {
  size?: FieldSize;
  name?: string;
  label?: string;
  required?: boolean;
  icon?: SvgIconInput;
  options: DropdownOption[];
  value?: string;
  defaultValue?: string;
  helperMessage?: string;
  errorMessage?: string;
  disabled?: boolean;
  view?: boolean;
  // true이면 모바일(1024px 이하)에서 옵션 패널이 바텀시트 스타일로 전환됨
  isSheet?: boolean;
  /** 바텀시트 헤더 스타일. basic: estimate-new 등, custom: 리드폼 팝업 */
  sheetHeaderType?: 'basic' | 'custom';
  onChange?: (value: string) => void;
  className?: string;
}

const iconSizeMap: Record<FieldSize, number> = {
  lg: 24,
  md: 20,
  sm: 16,
};

export default function Dropdown({
  size: sizeProp,
  name,
  label,
  required,
  icon,
  options,
  value: valueProp,
  defaultValue = '',
  helperMessage,
  errorMessage,
  disabled = false,
  view = false,
  isSheet = false,
  sheetHeaderType = 'basic',
  onChange,
  className,
}: DropdownProps) {
  const size = sizeProp ?? 'lg';
  const triggerId = useId();
  const listId = useId();
  const isControlled = valueProp !== undefined;

  const [internalValue, setInternalValue] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // isMobile 감지: Dropdown만 아는 정보, OptionList로 내려줌
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const value = isControlled ? valueProp! : internalValue;
  const selectedOption = options.find((o) => o.value === value) ?? null;
  const hasValue = !!selectedOption;
  const isLabelVisible = label && !hasValue;

  const isError = !!errorMessage;
  const isCompleted = !isOpen && hasValue && !isError && !disabled && !view;

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled && !view) setIsOpen((v) => !v);
  };

  const handleSelect = (option: DropdownOption) => {
    if (!isControlled) setInternalValue(option.value);
    onChange?.(option.value);
    setIsOpen(false);
  };

  const triggerCls = clsx(styles.trigger_button, styles[`size_${size}`], isOpen && !isError && styles.focused, isError && styles.error, isCompleted && styles.completed, disabled && styles.disabled, view && styles.view, className);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.field_wrap}>
        <button
          id={triggerId}
          type="button"
          className={triggerCls}
          onClick={handleToggle}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={listId}
        >
          {icon && (
            <span className={styles.icon_slot}>
              <Icon name={icon} size={iconSizeMap[size]} />
            </span>
          )}

          <div className={styles.trigger_area}>
            {label && (
              <span
                className={clsx(styles.trigger_label, !isLabelVisible && styles.trigger_label_hidden)}
                aria-hidden={!isLabelVisible}
              >
                {label}
                {required && <span className={styles.trigger_required}>*</span>}
              </span>
            )}

            <span className={styles.value_slot}>
              {selectedOption && selectedOption.label}
            </span>
          </div>

          <span className={clsx(styles.chevron, isOpen && styles.chevron_open)}>
            <Icon name={ArrowChevron} direction="down" size={24} color="var(--color-gray-900)" />
          </span>
        </button>

        {/* 옵션 패널: isSheet + isMobile 조합을 OptionList에 위임 */}
        {isOpen && (
          <OptionList
            listId={listId}
            triggerId={triggerId}
            size={size}
            options={options}
            value={value}
            onSelect={handleSelect}
            onClose={() => setIsOpen(false)}
            withScrollbar={true}
            isSheet={isSheet}
            isMobile={isMobile}
            sheetHeaderType={sheetHeaderType}
            label={label}
          />
        )}

        {name && <input type="hidden" name={name} value={value} />}
      </div>

      <FormMessage errorMessage={errorMessage ?? undefined} helperMessage={helperMessage ?? undefined} />
    </div>
  );
}
